// /services/metal-3d-printing/ (PrintingProcessPage.js). Code-owned since
// 2026-11-04; gatsby-node skips the Contentful service entry.
// GSC Aug–Oct 2026: 3 clicks, 2.2K impressions at position ~43 ("3d printing
// service metal", "3d printing metal service", "3d print service metal").
// "metal 3d printing" is 33,100/mo (KD 5), "metal 3d printing service" 1,900.
// Facts (truth.md): stainless steel only, by selective laser melting (SLM),
// 250 × 250 × 300 mm build area; alloy, properties, finish and schedule are
// confirmed in the quote; no blanket speed, accuracy, no-tooling,
// low-deformation or large-object promise. No metal price is published, so
// metal parts are quoted per project. Metal plating is the other route for a
// metal look and doesn't make a part solid metal.
const {
  pricing,
  photoRoot,
  reviewedDate,
  studios,
  delivery,
  noFile,
  hobby,
  quoteSteps,
  handoffStep,
} = require('./shared')
const printing = require('../printing-service')

const page = {
  path: '/services/metal-3d-printing/',
  crumb: 'Metal 3D printing',
  title: 'Metal 3D Printing Service | Stainless Steel SLM Parts',
  description:
    'Metal 3D printing service for solid stainless-steel parts by selective laser melting (SLM), in a 250 × 250 × 300 mm build area. Send a file for a written quote.',
  heading: 'Metal 3D Printing Service',
  eyebrow: 'Stainless steel 3D printing · Selective laser melting (SLM)',
  answer:
    'PrintAWorld offers metal 3D printing in stainless steel by selective laser melting (SLM): a laser melts fine steel powder layer by layer into a solid metal part. Parts fit a 250 × 250 × 300 mm (about 9.8 × 9.8 × 11.8 in) build area. Send a 3D file and a person reviews it, then quotes the alloy, post-processing, price and schedule. There’s no setup fee, and we ship to all 50 states.',
  hero: 'frame',
  faqHeading: 'What do people ask about metal 3D printing?',
  quoteText:
    'Send the file, the quantity, what the part does and any critical features. We’ll reply with the process, a price and a schedule.',
  reviewedDate,
}

const card = {
  label: 'Stainless steel',
  title: 'Metal 3D printing',
  body: 'Solid stainless-steel parts printed by selective laser melting.',
}

const photos = {
  handle: {
    url: `${photoRoot}/AD6mlil7dTu31GLwePvFZ/727702602a8e92d4d9c6b98db795bea7/3d_printed_metal_item.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A polished stainless-steel 3D printed crank handle with ball ends',
    title: 'Polished stainless handle',
    caption: 'A crank handle printed in stainless steel and polished.',
  },
  lattice: {
    url: `${photoRoot}/5xEzFr81UzTEaK4D6Xv8qp/0c84e8c60dcad94627ce38037300156c/WechatIMG9008.png`,
    width: 800,
    height: 706,
    alt: 'A stainless-steel 3D printed sphere with an open lattice shell',
    title: 'Lattice sphere',
    caption:
      'An open lattice that would be hard to machine or cast in one piece.',
  },
  parts: {
    url: `${photoRoot}/4RXW9XdjSFf3hq8nBvMBDQ/1ed55aa5cf8ec7bd2e0bc433b04abefe/3d_printed_stainless_steel_metal_objects.png`,
    width: 790,
    height: 583,
    alt: 'Three stainless-steel 3D printed parts: a bracket, a curved panel and a honeycomb block',
    title: 'Brackets, panels and honeycomb',
    caption: 'Thin walls and honeycomb cores printed in stainless steel.',
  },
  frame: {
    url: `${photoRoot}/Ym4O4DPpHw29H3P6Hhql4/4d79633e7415e1592ff34b386fff861e/3d_printed_metal_prototype.jpg`,
    width: 3000,
    height: 2119,
    alt: 'A rectangular stainless-steel 3D printed frame prototype',
    title: 'Metal frame prototype',
    caption: 'A rectangular frame prototype in stainless steel.',
  },
  gear: {
    ...printing.photos.gear,
    title: 'Impeller and tag',
    caption: 'A small stainless-steel impeller with a printed tag.',
  },
  models: {
    url: `${photoRoot}/27vbC6e85TKnVdKU3eB9Vr/c5a7e0de3e7d6af2c410102fa6a56d31/Screen_Shot_2019-04-18_at_11.03.08_PM.png`,
    width: 1430,
    height: 920,
    alt: 'Six small stainless-steel 3D printed architectural building models',
    title: 'Steel building models',
    caption: 'Small architectural models printed in stainless steel.',
  },
  printer: {
    url: `${photoRoot}/1GUEf8kS1AEZFsZ0Ik6yRo/59cbe8f7359f479dc55780a433e99f98/slm_3d_printer.jpg`,
    width: 1284,
    height: 1080,
    alt: 'An industrial selective laser melting (SLM) metal 3D printer',
    title: 'SLM metal printer',
    caption:
      'A selective laser melting (SLM) printer builds parts in a powder bed.',
  },
}

const intro = {
  eyebrow: 'Solid metal parts',
  heading: 'What is metal 3D printing?',
  paragraphs: [
    'Metal 3D printing builds a part from metal powder instead of plastic. In selective laser melting (SLM), a laser melts a thin layer of powder where the part should be, a new layer is spread on top, and it repeats until a solid metal part sits in the powder bed.',
    'Because it doesn’t need a mold or a cutting tool, it can make shapes that are hard to machine or cast: lattices, internal channels, organic forms and one-off parts. We print in stainless steel and quote each part after reviewing the file.',
  ],
}

const glance = {
  heading: 'Our metal 3D printing service at a glance',
  rows: [
    [
      'Process',
      'Selective laser melting (SLM), a metal powder-bed fusion process',
    ],
    ['Metal', 'Stainless steel. The quote confirms the exact alloy.'],
    ['Build area', '250 × 250 × 300 mm (about 9.8 × 9.8 × 11.8 in)'],
    ['Result', 'A solid stainless-steel part, not a plated surface'],
    ['Order size', 'One part or a batch that fits the build'],
    ['Setup fee', 'None for 3D printing'],
    ['Price', 'Quoted per project after a person reviews your file'],
    [
      'Post-processing',
      'Support removal and the finish you need, confirmed in the quote',
    ],
    ['Files', 'STEP or STP preferred; STL, OBJ and 3DM also work'],
    ['Studios', studios],
    ['Delivery', delivery],
    ['No file?', noFile],
  ],
}

const work = {
  heading: 'Stainless steel 3D printed parts.',
  photos: ['lattice', 'parts', 'handle', 'gear', 'models', 'printer'],
}

const before = [
  {
    id: 'slm',
    eyebrow: 'How it works',
    heading: 'How does selective laser melting (SLM) work?',
    index: '02 / THE PROCESS',
    paragraphs: [
      'The printer spreads a thin layer of stainless-steel powder across the build plate. A laser traces that slice of the part and melts the powder into solid metal. The plate drops, a fresh layer is spread, and the laser traces the next slice.',
      'When the build is done, the part is lifted out of the loose powder, separated from the plate and its supports are removed. Then it gets the finish in your quote.',
    ],
    list: [
      'Build area: 250 × 250 × 300 mm',
      'Material: stainless steel',
      'Supports are needed under overhangs and are removed after printing',
    ],
  },
  {
    id: 'uses',
    eyebrow: 'What it’s for',
    heading: 'What can you make with metal 3D printing?',
    index: '03 / THE USES',
    cards: [
      {
        label: 'U1',
        title: 'Metal prototypes',
        body: 'Brackets, housings and fittings in real metal, to test before tooling.',
      },
      {
        label: 'U2',
        title: 'Complex geometry',
        body: 'Lattices, internal channels and organic shapes that can’t be machined in one piece.',
      },
      {
        label: 'U3',
        title: 'Mold and tooling parts',
        body: 'Some mold inserts and tooling parts, after we review the design and how it’s used.',
      },
      {
        label: 'U4',
        title: 'Art and display pieces',
        body: 'Small sculptures, models and objects in solid steel.',
      },
    ],
  },
]

const contrast = {
  id: 'plating',
  eyebrow: 'Metal look or solid metal?',
  heading: 'Metal 3D printing or metal plating: which do you need?',
  paragraphs: [
    'Metal 3D printing makes the part itself out of solid stainless steel. Use it when the part has to be metal: for strength, heat, weight or feel.',
    'Metal plating adds a thin metal surface, such as chrome, gold or bronze, to a resin print. It looks like metal and usually costs less for display pieces, but the part underneath isn’t solid metal.',
  ],
  links: [
    {
      to: '/services/custom-3d-printing/',
      label: 'Plated and finished prints',
    },
    { to: '/services/sla-3d-printing/', label: 'SLA resin 3D printing' },
  ],
}

const fit = {
  eyebrow: 'Is it a fit?',
  heading: 'Is stainless steel 3D printing right for your part?',
  index: '04 / THE FIT',
  good: [
    'Stainless-steel prototypes and one-off parts',
    'Shapes that are hard to machine or cast: lattices, channels, organic forms',
    'Small batches that fit a 250 × 250 × 300 mm build',
    'Businesses, product teams and studios that want a person to review the file',
  ],
  other: [
    'Titanium, aluminum, copper or other alloys: we print stainless steel only.',
    'Parts bigger than 250 × 250 × 300 mm: consider machining, casting or a larger plastic print.',
    'A gold, silver or bronze look on a display piece: metal plating over resin usually costs less.',
    hobby,
  ],
}

const pricingBlock = {
  heading: 'How much does metal 3D printing cost?',
  index: '05 / THE PRICE',
  lead: {
    label: 'Metal parts are',
    value: 'Quoted',
    note: 'per project, after a person reviews your file.',
  },
  rows: [
    ['Stainless-steel SLM parts', 'Quoted per project'],
    ['Post-processing and finish', 'Quoted with the part'],
    ['3D modeling, if there’s no file', `From $${pricing.modelingFrom}`],
    ['3D scanning, to copy a part', `From $${pricing.scanningFrom}`],
    ['Setup fee', 'None'],
    ['Shipping, next-day available', 'Quoted to your ZIP'],
  ],
  note: 'Part volume, height in the build, supports, quantity and finish set the price. Your written quote confirms the alloy, price and schedule.',
}

const steps = {
  heading: 'How our metal 3D printing service works',
  index: '06 / THE PROCESS',
  items: [
    ...quoteSteps,
    {
      title: 'Print in stainless steel',
      body: 'Once you approve the quote, the part is printed by selective laser melting.',
    },
    {
      title: 'Remove supports and finish',
      body: 'The part comes off the build plate, supports are removed, and it gets the finish in your quote.',
    },
    handoffStep,
  ],
}

const checklist = [
  'Your 3D file: STEP or STP preferred, or STL, OBJ or 3DM',
  'Overall size and quantity',
  'What the part does and any critical features or fits',
  'The finish you need',
  'Your delivery ZIP code and deadline',
]

const faqs = [
  {
    question: 'What metals can you 3D print?',
    answer:
      'Stainless steel. Metal 3D printing can use other alloys, but PrintAWorld currently prints stainless steel only; the written quote confirms the exact alloy and its properties. For a gold, silver or bronze look, we print in resin and add metal plating.',
  },
  {
    question: 'What is selective laser melting (SLM)?',
    answer:
      'SLM is a metal powder-bed fusion process. A laser melts thin layers of metal powder, one on top of the other, into a solid metal part. SLM melts metal powder; SLA, which sounds similar, cures liquid resin.',
  },
  {
    question: 'How big can a metal 3D print be?',
    answer:
      'The build area is 250 × 250 × 300 mm (about 9.8 × 9.8 × 11.8 in). How much of it a part can use depends on its orientation, supports and shape; the quote confirms the fit.',
  },
  {
    question: 'How much does metal 3D printing cost?',
    answer:
      'Metal parts are quoted per project, after a person reviews the file. Part volume, height in the build, supports, quantity and finish set the price. There’s no setup fee for 3D printing.',
  },
  {
    question: 'Is a metal 3D printed part solid metal?',
    answer:
      'Yes. SLM melts the powder into a solid stainless-steel part. That’s different from metal plating, which coats a resin print in a thin layer of metal.',
  },
  {
    question: 'What tolerances can metal 3D printing hold?',
    answer:
      'It depends on the part’s size, shape and orientation, so we don’t promise a general tolerance. Tell us which features are critical and how the part fits, and the quote says what we can hold and whether any surfaces need extra finishing.',
  },
  {
    question: 'How long does metal 3D printing take?',
    answer:
      'It depends on the file, the number of parts, supports and finishing. Your written quote confirms the schedule; send your deadline with the file.',
  },
  {
    question: 'Do you ship metal 3D prints?',
    answer:
      'Yes, to all 50 states, with next-day shipping available once the part is ready. You can also pick up by appointment in Glendale, Queens or Brooklyn, New York.',
  },
]

module.exports = {
  page,
  card,
  photos,
  intro,
  glance,
  work,
  before,
  contrast,
  fit,
  pricing: pricingBlock,
  steps,
  checklist,
  faqs,
  schema: {
    name: 'Metal 3D Printing Service',
    serviceType:
      'Stainless-steel metal 3D printing by selective laser melting (SLM)',
    description:
      'Stainless-steel metal 3D printing by selective laser melting (SLM) in a 250 × 250 × 300 mm build area, with file review, quoted post-processing and shipping to all 50 states.',
    offer: 'Stainless-steel parts quoted per project after file review.',
  },
}
